/**
 * Commissions Service
 * Handles API calls for commission tracking and management
 */

class CommissionsService {
  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || '';
    this.requestCache = new Map();
    this.cacheTimeout = 30000; // 30 seconds cache
  }

  /**
   * Make authenticated API request
   */
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      // Add timeout to prevent hanging requests
      signal: options.signal || AbortSignal.timeout(5000),
    };

    const finalOptions = { ...defaultOptions, ...options };

    try {
      const response = await fetch(url, finalOptions);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = errorData;
        throw error;
      }
      
      const data = await response.json();
      
      // Ensure we always return a consistent structure
      return {
        success: data.success !== undefined ? data.success : true,
        data: data.data || data
      };
    } catch (error) {
      // Don't log timeout errors as they're expected in batching
      if (error.name !== 'TimeoutError' && error.name !== 'AbortError') {
        console.error(`[CommissionsService] Request failed:`, error);
      }
      throw error;
    }
  }

  /**
   * Get commission for a specific trip
   */
  async getTripCommission(tripId) {
    try {
      // Check cache first
      const cacheKey = `trip_${tripId}`;
      const cached = this.requestCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }

      const result = await this.makeRequest(`/api/trips/${tripId}/commission`);
      
      // Cache successful result
      this.requestCache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });
      
      return result;
    } catch (error) {
      // Return null on error - commission data is optional
      return {
        success: false,
        data: null,
        error: error.message
      };
    }
  }

  /**
   * Get commissions for multiple trips in batches with delay
   */
  async getTripCommissionsBatch(tripIds, batchSize = 5, delayBetweenBatches = 200) {
    const results = new Map();
    
    // Process trips in batches
    for (let i = 0; i < tripIds.length; i += batchSize) {
      const batch = tripIds.slice(i, i + batchSize);
      
      await Promise.all(
        batch.map(async (tripId) => {
          try {
            const commission = await this.getTripCommission(tripId);
            if (commission.success && commission.data) {
              results.set(tripId, commission.data);
            }
          } catch (error) {
            // Silently skip failed commission fetches
            console.debug(`Skipping commission for trip ${tripId}`);
          }
        })
      );
      
      // Add delay between batches to avoid overwhelming backend
      if (i + batchSize < tripIds.length) {
        await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
      }
    }
    
    return results;
  }

  /**
   * Get all commissions with filtering
   */
  async getAllCommissions(filters = {}) {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = `/api/commission/tracking${queryParams ? `?${queryParams}` : ''}`;
      return await this.makeRequest(endpoint);
    } catch (error) {
      console.error('[CommissionsService] getAllCommissions failed:', error);
      return {
        success: false,
        data: [],
        error: error.message
      };
    }
  }

  /**
   * Get commission summary/analytics
   */
  async getCommissionSummary(period = 'current_month') {
    try {
      return await this.makeRequest(`/api/commission/summary?period=${period}`);
    } catch (error) {
      console.error('[CommissionsService] getCommissionSummary failed:', error);
      return {
        success: false,
        data: {
          total: 0,
          pending: 0,
          paid: 0,
          total_amount: 0
        },
        error: error.message
      };
    }
  }

  /**
   * Get commission analytics
   */
  async getCommissionAnalytics(period = 'current_month') {
    try {
      return await this.makeRequest(`/api/commission/analytics?period=${period}`);
    } catch (error) {
      console.error('[CommissionsService] getCommissionAnalytics failed:', error);
      return {
        success: false,
        data: null,
        error: error.message
      };
    }
  }

  /**
   * Create or update commission
   */
  async updateCommission(tripId, commissionData) {
    try {
      return await this.makeRequest(`/api/trips/${tripId}/commission`, {
        method: 'PUT',
        body: JSON.stringify(commissionData),
      });
    } catch (error) {
      console.error('[CommissionsService] updateCommission failed:', error);
      throw error;
    }
  }

  /**
   * Create commission booking
   */
  async createCommissionBooking(commissionData) {
    try {
      return await this.makeRequest('/api/commission/booking', {
        method: 'POST',
        body: JSON.stringify(commissionData),
      });
    } catch (error) {
      console.error('[CommissionsService] createCommissionBooking failed:', error);
      throw error;
    }
  }

  /**
   * Update commission booking
   */
  async updateCommissionBooking(bookingId, commissionData) {
    try {
      return await this.makeRequest(`/api/commission/booking/${bookingId}`, {
        method: 'PUT',
        body: JSON.stringify(commissionData),
      });
    } catch (error) {
      console.error('[CommissionsService] updateCommissionBooking failed:', error);
      throw error;
    }
  }

  /**
   * Delete commission booking
   */
  async deleteCommissionBooking(bookingId) {
    try {
      return await this.makeRequest(`/api/commission/booking/${bookingId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('[CommissionsService] deleteCommissionBooking failed:', error);
      throw error;
    }
  }

  /**
   * Clear the request cache
   */
  clearCache() {
    this.requestCache.clear();
  }

  /**
   * Clear specific trip commission from cache
   */
  clearTripCache(tripId) {
    this.requestCache.delete(`trip_${tripId}`);
  }
}

// Export singleton instance
export default new CommissionsService();

